// ── BASE ACRES (FSA-156EZ) — the Coverage Advisor's per-farmer FSA farm record ─────────────
//
// Upload a customer's FSA-156EZ, see what it says, save it to that farmer, print one page.
// Every figure comes from src/fsa156ez.js (parser, identities, the one model) and the sheet
// from src/fsa156ezPrint.js; this file only draws them. App.jsx hands in the I/O it owns —
// the PDF text reader (isEvalSupported:false lives there, check-pdf-eval), the Supabase reads,
// the original-document store and the RPC — so the panel never grows its own copies.
import React, { useEffect, useMemo, useState } from "react";
import * as FSA156 from "./fsa156ez";
import * as FSA156P from "./fsa156ezPrint";
import * as CCC866 from "./ccc866";
import { Ccc866Section } from "./ccc866Panel";
import { writePrintWindow } from "./printBackBar";

const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const acres = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const CROP_LABEL = { CORN: "Corn", SOYBEANS: "Soybeans", WHEAT: "Wheat", OATS: "Oats" };
const cropLabel = (c) => CROP_LABEL[c] || titleCase(c);
const missingSchema = (e) => /42P01|PGRST205|PGRST202|does not exist|schema cache|Could not find the (table|function)/i.test(String((e && e.message) || e));

// `part` splits the panel across Farm Programs' tabs: "base" draws the FSA-156EZ record,
// "ccc866" the CCC-866 elections, and null (the default) draws both. The data is read the
// same way in every case, because the CCC-866 section checks itself against the 156EZ.
// `onFarmerChange` tells the page which farmer is picked, so its other tabs open on them.
export function FsaBaseAcresPanel({ farmers = [], initialFarmerId = null, isMobile = false, api, ui, part = null, onFarmerChange = null }) {
  const { T, uiCard, uiLabel, uiNum, UiSpinner } = ui;
  const [farmerId, setFarmerId] = useState(initialFarmerId || "");
  useEffect(() => { if (initialFarmerId) setFarmerId(initialFarmerId); }, [initialFarmerId]);
  useEffect(() => { if (onFarmerChange) onFarmerChange(farmerId || null); }, [farmerId, onFarmerChange]);
  const showBase = part !== "ccc866", show866 = part !== "base";
  const [q, setQ] = useState("");
  const [years, setYears] = useState([]);       // fsa_156ez_uploads rows, newest first — ONE ROW PER YEAR PER ADMIN COUNTY (sql/116)
  const yearList = useMemo(() => [...new Set(years.map((u) => u.crop_year))], [years]);
  const [year, setYear] = useState(null);
  const [stored, setStored] = useState(null);   // { upload, farms, tracts, base }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [pending, setPending] = useState(null); // { file, doc, payload, rc }
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState({});
  const [reload, setReload] = useState(0);
  const [m866, setM866] = useState(null);      // the stored CCC-866 election model, reported by <Ccc866Section>

  const farmer = (farmers || []).find((f) => f && f.id === farmerId) || null;
  const farmerName = farmer ? (farmer.name || farmer.farmer_name || "") : "";
  // ⚠ ONE ROW PER FARMER ID. The farmers list is split per policy card, so one customer arrives
  // several times under the SAME id — duplicate React keys left stale rows on screen and the
  // search looked as if it did not filter at all.
  const sortedFarmers = useMemo(() => [...new Map((farmers || []).filter((f) => f && f.id).map((f) => [f.id, f])).values()]
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))), [farmers]);
  const pickList = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? sortedFarmers.filter((f) => String(f.name || "").toLowerCase().includes(s)).slice(0, 12) : [];
  }, [q, sortedFarmers]);

  // ── read what is on file ──
  useEffect(() => {
    let alive = true;
    setStored(null); setErr(null); setYears([]);
    if (!farmerId) return undefined;
    setLoading(true);
    (async () => {
      try {
        const ups = await api.fetchRows("fsa_156ez_uploads", { filters: [`farmer_id=eq.${farmerId}`], order: "crop_year.desc", fresh: true });
        if (!alive) return;
        setSchemaMissing(false);
        setYears(ups || []);
        const y = (ups || []).some((u) => u.crop_year === year) ? year : ((ups || [])[0] ? ups[0].crop_year : null);
        const yearRows = (ups || []).filter((u) => u.crop_year === y);
        setYear(y);
        if (y == null) { setLoading(false); return; }
        const f = [`farmer_id=eq.${farmerId}`, `crop_year=eq.${y}`];
        const [farms, tracts, base] = await Promise.all([
          api.fetchRows("fsa_farm_records", { filters: f, fresh: true }),
          api.fetchRows("fsa_tract_records", { filters: f, fresh: true }),
          api.fetchRows("fsa_base_acres", { filters: f, fresh: true }),
        ]);
        if (!alive) return;
        // every county on file for the year, combined: the screen and the print show all of them, grouped by county
        setStored({ upload: FSA156.mergeUploads156(yearRows), uploads: yearRows, farms: farms || [], tracts: tracts || [], base: base || [] });
      } catch (e) {
        if (!alive) return;
        if (missingSchema(e)) setSchemaMissing(true); else setErr(String((e && e.message) || e));
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [farmerId, year, reload]);

  // The SIGNED CCC-866 election (same program year) outranks the 156EZ's election-or-default text.
  const overrides866 = useMemo(() => (m866 && stored && stored.upload && m866.program_year === stored.upload.crop_year ? CCC866.electionOverrides(m866) : null), [m866, stored]);
  const model = useMemo(() => (stored ? FSA156.baseAcreModel({ ...stored, overrides866 }) : null), [stored, overrides866]);
  const preview = useMemo(() => (pending ? FSA156.baseAcreModel(FSA156.storedFromPayload(pending.payload)) : null), [pending]);
  const shown = preview || model;

  // ── upload: read, parse, reconcile — nothing is saved until the agent says so ──
  const onFile = async (file) => {
    if (!file) return;
    setErr(null); setPending(null); setBusy("Reading the 156EZ…");
    try {
      const pages = await api.readPdfPages(file);
      if (!pages || !pages.length) throw new Error("This PDF has no text layer to read (a scan?) — it cannot be read automatically.");
      const text = pages.map((items) => FSA156.linesFromTextItems(items).join("\n")).join("\n");
      const doc = FSA156.parse156ez(text);
      if (!doc.farms.length) throw new Error("No FSA-156EZ farm section was found in this file. Is it an FSA-156EZ?");
      const noAdmin = FSA156.missingAdmin156(doc);
      if (noAdmin.length) throw new Error(`Not saved: the administrative state/county could not be read for FSA farm${noAdmin.length === 1 ? "" : "s"} ${noAdmin.join(", ")}. Each document is filed under its FSA administrative county, so one whose county cannot be read cannot be stored.`);
      const rc = FSA156.reconcile156ez(doc);
      const payload = FSA156.rowsFor156ez(doc, { farmerId, fileName: file.name });
      setPending({ file, doc, payload, rc });
    } catch (e) { setErr(String((e && e.message) || e)); }
    setBusy(null);
  };

  const save = async () => {
    if (!pending || !farmerId) return;
    setErr(null); setBusy("Saving…");
    try {
      const orig = await api.storeOriginal({ file: pending.file, cropYear: pending.doc.crop_year, docType: "fsa_156ez", farmerId });
      if (orig && orig.status === "failed") throw new Error("The PDF could not be stored: " + orig.error);
      const body = { ...pending.payload, document_id: (orig && orig.id) || null, storage_path: (orig && orig.path) || null };
      await api.rpc("fsa_156ez_replace", { p: body });
      setPending(null); setYear(pending.doc.crop_year); setReload((n) => n + 1);
    } catch (e) {
      if (missingSchema(e)) setErr("Saving needs the database function from sql/110 and sql/116 (fsa_156ez_replace), which are not applied yet. The read above is correct; nothing was saved.");
      else setErr(String((e && e.message) || e));
    }
    setBusy(null);
  };

  const printSummary = async () => {
    if (!shown) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const logo = await api.logoSrc();
    const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    writePrintWindow(w, FSA156P.render156ezSummaryHtml(shown, { farmerName, logoSrc: logo, printedOn: today }));
  };

  const exportCsv = () => {
    if (!stored) return;
    const csv = FSA156.csv156ez(stored.farms.map((f) => ({ ...f, crop_year: f.crop_year })), stored.tracts, stored.base);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `base-acres-${(farmerName || "farmer").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${year}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const viewPdf = async (p) => {
    if (!p) return;
    try { window.open(await api.signedUrl(p), "_blank"); } catch (e) { setErr(String((e && e.message) || e)); }
  };

  // ── pieces ──
  const card = { ...uiCard(isMobile ? 12 : 16), marginBottom: 14 };
  const btn = (primary, disabled) => ({ fontSize: 12.5, fontWeight: 800, padding: "7px 14px", borderRadius: 8, cursor: disabled ? "default" : "pointer",
    border: primary ? "none" : `1px solid ${T.lineStrong}`, background: disabled ? "#b7c7b7" : primary ? T.green : "#fff", color: primary ? "#fff" : T.inkSoft });
  const Badge = ({ c }) => {
    if (!c || !c.program) return null;
    const def = c.source === "default";
    return <span title={def ? "FSA default — no election was made by the producer" : c.source === "ccc866" ? `Elected on CCC-866${c.signed_date ? ", signed " + c.signed_date : ""}` : "Elected by the producer"}
      style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 10, marginLeft: 6, whiteSpace: "nowrap",
        border: `1px solid ${def ? "#e2c98f" : "#9cc79c"}`, background: def ? "#fdf8ec" : "#eaf4ea", color: def ? "#8a6d1f" : T.green }}>
      {def ? "Default " : "Elected "}{c.program}</span>;
  };

  const header = (
    <div style={card}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0, flex: "1 1 260px" }}>
          <div style={uiLabel}>Farmer</div>
          {farmer ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 3 }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: T.ink, fontFamily: T.serif }}>{farmerName}</span>
              <button onClick={() => { setFarmerId(""); setPending(null); setQ(""); }} style={{ ...btn(false), fontSize: 11, padding: "3px 9px" }}>change</button>
            </div>
          ) : (
            <div style={{ position: "relative", maxWidth: 420, marginTop: 4 }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a farmer’s name…" autoComplete="off"
                style={{ width: "100%", fontSize: 14, padding: "9px 12px", borderRadius: 8, border: `1px solid ${T.lineStrong}`, boxSizing: "border-box" }} />
              {pickList.length > 0 && (
                <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, marginTop: 4, maxHeight: 260, overflowY: "auto", background: "#fff" }}>
                  {pickList.map((f) => (
                    <div key={f.id} onClick={() => { setFarmerId(f.id); setQ(""); }} style={{ padding: "7px 12px", fontSize: 13.5, cursor: "pointer", borderBottom: `1px solid ${T.lineSoft}` }}>{f.name}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {farmer && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {yearList.length > 1 && (
              <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} aria-label="Crop year"
                style={{ fontSize: 12.5, padding: "6px 8px", borderRadius: 7, border: `1px solid ${T.lineStrong}` }}>
                {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {showBase && <>
            <label style={{ ...btn(true, !!busy), display: "inline-block" }}>
              {stored ? "Upload a newer 156EZ" : "Upload FSA-156EZ"}
              <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} disabled={!!busy}
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; onFile(f); }} />
            </label>
            <button onClick={printSummary} disabled={!shown} style={btn(false, !shown)}>Print base acre summary</button>
            <button onClick={exportCsv} disabled={!stored || !!pending} style={btn(false, !stored || !!pending)}>Export CSV</button>
            {stored && stored.upload && !pending && (stored.upload.storage_paths || []).map((p, i, a) => <button key={p} onClick={() => viewPdf(p)} style={btn(false)}>{a.length > 1 ? `View 156EZ ${i + 1} of ${a.length}` : "View 156EZ"}</button>)}
            </>}
          </div>
        )}
      </div>
      {busy && <div style={{ marginTop: 10, fontSize: 12.5, color: T.muted }}><UiSpinner />{busy}</div>}
      {err && <div style={{ marginTop: 10, fontSize: 12.5, color: T.danger }}>{err}</div>}
      {schemaMissing && <div style={{ marginTop: 10, fontSize: 12.5, color: T.warn }}>The base-acre tables (sql/110) are not in the database yet. A 156EZ can still be read and checked here; it cannot be saved.</div>}
    </div>
  );

  // the reconciliation of a just-read document, before it is saved
  const review = pending && (() => {
    const { doc, rc } = pending;
    // Saving replaces ONLY the administrative counties this document contains; other counties on file stay.
    const pairs = FSA156.adminPairs156(doc);
    const onFile = years.filter((u) => u.crop_year === doc.crop_year);
    const hit = (u) => pairs.some((p) => p.admin_state === u.admin_state && p.admin_county === u.admin_county);
    const replaced = onFile.filter(hit), kept = onFile.filter((u) => !hit(u));
    const replacing = replaced[0];
    const older = replaced.find((u) => u.prepared_date && doc.prepared_date && doc.prepared_date < u.prepared_date);
    const op = [...new Set(doc.farms.map((f) => f.operator_name).filter(Boolean))];
    const nChecks = rc.checks.filter((c) => !c.note).length;
    const notes = rc.checks.filter((c) => c.note);
    return (
      <div style={{ ...card, border: `1px solid ${rc.ok ? "#9cc79c" : "#e2c98f"}`, background: rc.ok ? "#f4faf4" : "#fdf8ec" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: rc.ok ? T.green : "#8a6d1f" }}>
          {rc.ok ? "Read and reconciled — not saved yet" : "Read, but the document does not reconcile — check before saving"}
        </div>
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 4, lineHeight: 1.5 }}>
          {pending.file.name} · crop year <b>{doc.crop_year || "unknown"}</b> · prepared by FSA {doc.prepared_date || "—"} · operator on the record: <b>{op.join(", ") || "—"}</b>
          <br />{doc.farms.length} FSA farms · {doc.farms.reduce((a, f) => a + f.tracts.length, 0)} tracts · {nChecks - rc.failures.length} of {nChecks} checks agree
          (tract base sums to each farm, crops to each TOTAL, tract land to farm land, every page read){doc.unparsed.length ? ` · ${doc.unparsed.length} line(s) not read` : " · every line read"}
        </div>
        <div style={{ fontSize: 12.5, marginTop: 6, color: T.body }}>Administrative {pairs.length === 1 ? "county" : "counties"} in this document: <b>{pairs.map((p) => titleCase(p.admin_county)).join(", ")}</b>.</div>
        {replaced.length > 0 && <div style={{ fontSize: 12.5, marginTop: 6, color: older ? T.danger : T.body }}>
          Saving <b>replaces</b> the {doc.crop_year} record for {replaced.map((u) => titleCase(u.admin_county)).join(", ")}{replacing.prepared_date ? ` (prepared ${replacing.prepared_date})` : ""}.{older ? " ⚠ This document was prepared EARLIER than the one on file." : ""}
        </div>}
        {kept.length > 0 && <div style={{ fontSize: 12.5, marginTop: 4, color: T.muted }}>The {doc.crop_year} record for {kept.map((u) => titleCase(u.admin_county)).join(", ")} stays as it is.</div>}
        {rc.failures.length > 0 && <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: "#6b4f12" }}>{rc.failures.slice(0, 12).map((c, i) => <li key={i}>{c.detail}</li>)}{rc.failures.length > 12 && <li>… and {rc.failures.length - 12} more</li>}</ul>}
        {doc.unparsed.length > 0 && <details style={{ marginTop: 6, fontSize: 12 }}><summary>Lines not read ({doc.unparsed.length})</summary>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: T.muted }}>{doc.unparsed.slice(0, 40).map((u, i) => <li key={i}>page {u.page} · {u.where}: <code>{u.line}</code></li>)}</ul></details>}
        {notes.length > 0 && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>As printed on the record: {notes.map((c) => c.detail).join("; ")}.</div>}
        {doc.crop_year == null && <div style={{ fontSize: 12.5, color: T.danger, marginTop: 6 }}>The crop year could not be read, so this cannot be saved.</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button onClick={save} disabled={!!busy || doc.crop_year == null || !farmerId || schemaMissing} style={btn(true, !!busy || doc.crop_year == null || schemaMissing)}>
            {rc.ok ? `Save to ${farmerName}` : `Save anyway, with the warning`}
          </button>
          <button onClick={() => setPending(null)} style={btn(false)}>Discard</button>
        </div>
      </div>
    );
  })();

  const summary = shown && (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>Base acres · {shown.crop_year}</div>
        <div style={{ fontSize: 12, color: T.muted }}>
          {preview ? "preview of the document just read" : `from the FSA-156EZ prepared ${shown.prepared_date || "—"}${shown.file_name ? ` · ${shown.file_name}` : ""}`}
        </div>
      </div>
      {!preview && (shown.counties || []).length > 0 && <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>On file for {shown.crop_year}: {shown.counties.map((c) => `${titleCase(c.admin_county)}${c.farm_count != null ? ` (${c.farm_count} farm${c.farm_count === 1 ? "" : "s"})` : ""}`).join(" · ")}. All counties are combined below and grouped by county.</div>}
      {!preview && shown.identity_ok === false && <div style={{ fontSize: 12, color: "#6b4f12", background: "#fdf8ec", border: "1px solid #e6c98a", borderRadius: 6, padding: "6px 9px", marginBottom: 10 }}>
        This record was saved with {shown.identity_failures.length} reconciliation warning(s){shown.unparsed_count ? ` and ${shown.unparsed_count} unread line(s)` : ""}: {shown.identity_failures.slice(0, 3).join("; ")}{shown.identity_failures.length > 3 ? "; …" : ""}</div>}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
        {[["Base acres", acres(shown.total_base)], ["FSA farms", shown.farm_count], ["Tracts", shown.tract_count], ["Cropland", acres(shown.cropland)]].map(([l, v]) => (
          <div key={l} style={{ background: T.surfaceAlt, border: `1px solid ${T.lineSoft}`, borderRadius: 10, padding: "9px 12px" }}>
            <div style={uiLabel}>{l}</div><div style={{ ...uiNum, fontSize: 20, fontWeight: 800, color: T.ink, marginTop: 2 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
          <thead><tr>{["Crop", "Base acres", "PLC yield", "FSA farms", "Program on the record"].map((h, i) => <th key={h} style={{ ...uiLabel, textAlign: i === 0 || i === 4 ? "left" : "right", padding: "6px 8px", borderBottom: `1.5px solid ${T.inkSoft}` }}>{h}</th>)}</tr></thead>
          <tbody>{shown.totals.map((t) => {
            const progs = {};
            for (const f of shown.farms) { const c = f.byCrop[t.crop]; if (c && c.program) { const k = (c.source === "default" ? "Default " : "Elected ") + c.program; progs[k] = (progs[k] || 0) + 1; } }
            return (
              <tr key={t.crop} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                <td style={{ padding: "7px 8px", fontWeight: 700, color: T.ink }}>{cropLabel(t.crop)}</td>
                <td style={{ ...uiNum, padding: "7px 8px", textAlign: "right", fontWeight: 800 }}>{acres(t.base_acres)}</td>
                <td style={{ ...uiNum, padding: "7px 8px", textAlign: "right" }} title="Weighted by base acres across FSA farms — a summary, not one FSA figure">{t.plc_yield_weighted == null ? "—" : t.plc_yield_weighted} <span style={{ color: T.faint, fontSize: 11 }}>wtd</span></td>
                <td style={{ ...uiNum, padding: "7px 8px", textAlign: "right" }}>{t.farms}</td>
                <td style={{ padding: "7px 8px", fontSize: 12, color: T.body }}>{Object.entries(progs).map(([k, n]) => `${k} on ${n}`).join(" · ") || "—"}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      {!shown.any_elected && shown.any_default && <div style={{ fontSize: 12, color: "#6b4f12", marginTop: 10 }}>
        No ARC/PLC election is printed on this record — every program shown is <b>FSA’s default</b>, which applies when the producer does not elect. It is not the producer’s election.</div>}
      {shown.farms_without_base.length > 0 && <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>No base acres on FSA farm{shown.farms_without_base.length === 1 ? "" : "s"} {shown.farms_without_base.join(", ")}.</div>}
    </div>
  );

  const farmList = shown && (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, marginBottom: 8 }}>FSA farms <span style={{ fontWeight: 600, color: T.muted, fontSize: 12 }}>· tap a farm for its tracts and owners</span></div>
      {shown.farms.map((f) => {
        const isOpen = !!open[f.id];
        return (
          <div key={f.id} style={{ border: `1px solid ${T.lineSoft}`, borderRadius: 10, marginBottom: 8, background: isOpen ? T.surfaceAlt : "#fff" }}>
            <div onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))} role="button" aria-expanded={isOpen}
              style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "9px 12px", cursor: "pointer" }}>
              <span style={{ color: T.faint, width: 10 }}>{isOpen ? "▾" : "▸"}</span>
              <div style={{ minWidth: 110 }}>
                <div style={{ fontWeight: 800, color: T.ink }}>Farm {f.farm_number}</div>
                <div style={{ fontSize: 11.5, color: T.muted }}>{titleCase(f.admin_county)}{f.out_of_county.length ? <span style={{ color: "#8a6d1f" }}> · tracts in {f.out_of_county.map(titleCase).join(", ")}</span> : null}</div>
              </div>
              <div style={{ fontSize: 12, color: T.muted, minWidth: 120 }}><span style={uiNum}>{acres(f.cropland)}</span> cropland · {f.tracts.length} tract{f.tracts.length === 1 ? "" : "s"}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: "1 1 300px" }}>
                {Object.keys(f.byCrop).length ? shown.crops.filter((c) => f.byCrop[c]).map((c) => {
                  const x = f.byCrop[c];
                  return <span key={c} style={{ fontSize: 12, border: `1px solid ${T.line}`, borderRadius: 8, padding: "3px 8px", background: "#fff", whiteSpace: "nowrap" }}>
                    <b>{cropLabel(c)}</b> <span style={uiNum}>{acres(x.base_acres)}</span> ac · PLC {x.plc_yield == null ? "—" : x.plc_yield}<Badge c={x} /></span>;
                }) : <span style={{ fontSize: 12, color: T.faint }}>No base acres</span>}
              </div>
              <div style={{ ...uiNum, fontWeight: 800, color: T.ink, marginLeft: "auto" }}>{Object.keys(f.byCrop).length ? acres(f.total_base) + " ac" : ""}</div>
            </div>
            {isOpen && (
              <div style={{ padding: "0 12px 10px 34px", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
                  <thead><tr>{["Tract", "Located in", "Owners", "Cropland", ...shown.crops.map(cropLabel)].map((h, i) => <th key={h} style={{ ...uiLabel, textAlign: i < 3 ? "left" : "right", padding: "5px 6px", borderBottom: `1px solid ${T.line}` }}>{h}</th>)}</tr></thead>
                  <tbody>{f.tracts.map((t) => (
                    <tr key={t.tract_number} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                      <td style={{ padding: "5px 6px", fontWeight: 700 }}>{t.tract_number}</td>
                      <td style={{ padding: "5px 6px", color: t.location_county !== f.admin_county ? "#8a6d1f" : T.body }}>{titleCase(t.location_county)}{t.location_state && t.location_state !== f.admin_state ? `, ${titleCase(t.location_state)}` : ""}</td>
                      <td style={{ padding: "5px 6px", color: T.body }}>{t.owners || "—"}</td>
                      <td style={{ ...uiNum, padding: "5px 6px", textAlign: "right" }}>{acres(t.cropland)}</td>
                      {shown.crops.map((c) => <td key={c} style={{ ...uiNum, padding: "5px 6px", textAlign: "right" }}>{t.byCrop[c] ? <>{acres(t.byCrop[c].base_acres)} <span style={{ color: T.faint, fontSize: 11 }}>· {t.byCrop[c].plc_yield}</span></> : "—"}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      {showBase && <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 12 }}>
        Upload a customer’s FSA-156EZ to keep their base acres, PLC yields and ARC/PLC elections on file. The document is read line by line and checked against its own totals before it is saved.
      </div>}
      {header}
      {showBase && review}
      {/* Mounted on the Base acres tab too, only hidden: the elections it reads (onModel) are what
          the base-acre table prints as each crop's program, so it must load whichever tab is open. */}
      {farmer && <div style={show866 ? undefined : { display: "none" }}><Ccc866Section farmer={farmer} farmerId={farmerId} farmerName={farmerName} model156={model} api={api} ui={ui} isMobile={isMobile} onModel={setM866} /></div>}
      {showBase && farmer && loading && <div style={{ padding: 16, color: T.muted }}><UiSpinner />Loading base acres…</div>}
      {showBase && farmer && !loading && !shown && !pending && !schemaMissing && !err && <div style={{ ...card, color: T.muted, fontSize: 13 }}>No base acres on file for {farmerName}. Upload their FSA-156EZ above.</div>}
      {showBase && summary}
      {showBase && farmList}
    </div>
  );
}
